import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DerivedPriceChart from '../components/DerivedPriceChart';
import ChartErrorBoundary from '../components/ChartErrorBoundary';
import ArticleAnalyzeModal from '../components/ArticleAnalyzeModal';
import LivePriceModule from '../components/LivePriceModule';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Loader2, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Activity,
  BarChart3,
  Zap,
  Brain,
  Users,
  Landmark,
  RefreshCw,
  ChevronDown,
  ExternalLink,
  Newspaper,
  Clock,
  Tag,
  FileSearch,
  Building2,
  Headphones,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Info,
  ChevronUp,
  ChevronRight,
  Star,
  Bell,
  Lock,
  Plus,
  Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTour } from '../contexts/TourContext';
import UpgradeModal from '../components/UpgradeModal';
import SEOHead, { getStockPageSchema } from '../components/SEOHead';
import { findStockSector, getRelatedStocks } from '../utils/stockSectors';
import { getCompanyDescriptionWithFallback } from '../utils/companyDescriptions';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Signal indicators
const SignalBadge = ({ label, value, color, icon: Icon }) => (
  <div 
    className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
    style={{ backgroundColor: `${color}10`, border: `1px solid ${color}25` }}
  >
    {Icon && <Icon className="h-3.5 w-3.5" style={{ color }} />}
    <span className="text-[10px] uppercase tracking-wider" style={{ color: '#71717a' }}>{label}</span>
    <span className="text-xs font-semibold" style={{ color }}>{value}</span>
  </div>
);

// Modular metric card
const MetricCard = ({ title, value, subtitle, color, icon: Icon }) => (
  <div 
    className="p-4 rounded-xl"
    style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
  >
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className="h-4 w-4" style={{ color: '#52525b' }} />}
      <span className="text-[10px] uppercase tracking-wider" style={{ color: '#52525b' }}>{title}</span>
    </div>
    <div className="text-xl font-bold mb-1" style={{ color }}>{value}</div>
    {subtitle && <p className="text-[11px]" style={{ color: '#71717a' }}>{subtitle}</p>}
  </div>
);

const StockDetail = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isPro, token } = useAuth();
  const { pendingContinue, pendingTourId, resumeAfterNavigation } = useTour();
  
  // Data states - DERIVED DATA ONLY (Tier 1 Compliance)
  const [chartData, setChartData] = useState([]);  // [{date, value}] - daily close only
  const [anomaly, setAnomaly] = useState(null);
  const [anomalyLoaded, setAnomalyLoaded] = useState(false);
  const [overview, setOverview] = useState(null);
  const [fastContext, setFastContext] = useState(null);
  const [deepContext, setDeepContext] = useState(null);
  
  // Signal states - DERIVED SIGNALS ONLY (no raw data)
  const [politicalSignal, setPoliticalSignal] = useState(null);
  const [institutionalSignal, setInstitutionalSignal] = useState(null);
  
  // Summary states - NEW: Derived summaries (Tier 1 Compliance)
  const [institutionalSummary, setInstitutionalSummary] = useState(null);
  const [politicalSummary, setPoliticalSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  
  const [finnhubNews, setFinnhubNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState(null);
  
  // Watchlist & Alert states
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [fastLoading, setFastLoading] = useState(true);
  const [deepLoading, setDeepLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  
  // Status states
  const [newsStatus, setNewsStatus] = useState('pending');
  const [newsMessage, setNewsMessage] = useState(null);
  const [aiStatus, setAiStatus] = useState('pending');
  const [aiMessage, setAiMessage] = useState(null);
  
  // UI states - Removed timeframe/indicator controls (only daily close allowed)
  const [showDeep, setShowDeep] = useState(false);
  const [articleScanUrl, setArticleScanUrl] = useState('');
  const [articleScanFallback, setArticleScanFallback] = useState(null); // Fallback headline/summary
  const [showArticleScan, setShowArticleScan] = useState(false);
  const [articleAnalysisResult, setArticleAnalysisResult] = useState(null);
  
  // Voice AI states
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVoiceModule, setShowVoiceModule] = useState(false);
  const [showConfidenceBreakdown, setShowConfidenceBreakdown] = useState(false);

  // Scroll to top when component mounts (on navigation from dashboard)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [symbol]);

  // Open article scanner with URL prefilled and optional fallback data
  const openArticleScanner = useCallback((url, fallbackData = null) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Clicked AI Article Analysis button');
      console.log('Opening scanner with URL:', url);
      console.log('Fallback data:', fallbackData);
    }
    setArticleScanUrl(url || '');
    setArticleScanFallback(fallbackData);
    setShowArticleScan(true);
  }, []);

  // Check if symbol is in watchlist (Pro users only)
  useEffect(() => {
    if (!isPro || !token) return;
    
    const checkWatchlist = async () => {
      try {
        const response = await axios.get(`${API_BASE}/watchlist`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const symbols = response.data.symbols || [];
        setIsInWatchlist(symbols.includes(symbol.toUpperCase()));
      } catch (error) {
        // Silently fail - watchlist check is optional
      }
    };
    
    checkWatchlist();
  }, [isPro, token, symbol]);

  /**
   * Tour continuation: Resume the tour after navigation from Dashboard.
   * The AppTour component handles the actual resumption, but we add a data
   * attribute to help the tour know the page is ready.
   */
  useEffect(() => {
    if (pendingContinue && pendingTourId) {
      // Add a marker that the page is ready for tour resume
      // The AppTour component will detect this and resume
      const marker = document.createElement('div');
      marker.id = 'stock-detail-ready';
      marker.setAttribute('data-testid', 'stock-header');
      marker.style.display = 'none';
      document.body.appendChild(marker);
      
      return () => {
        const existing = document.getElementById('stock-detail-ready');
        if (existing) existing.remove();
      };
    }
  }, [pendingContinue, pendingTourId]);

  // Toggle watchlist
  const handleWatchlistToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!isPro) {
      setUpgradeFeature('Watchlists');
      setShowUpgradeModal(true);
      return;
    }
    
    setWatchlistLoading(true);
    try {
      if (isInWatchlist) {
        await axios.post(`${API_BASE}/watchlist/remove`, 
          { symbol: symbol.toUpperCase() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsInWatchlist(false);
        toast.success(`Removed ${symbol} from watchlist`);
      } else {
        await axios.post(`${API_BASE}/watchlist/add`,
          { symbol: symbol.toUpperCase() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsInWatchlist(true);
        toast.success(`Added ${symbol} to watchlist`);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeFeature('Watchlists');
        setShowUpgradeModal(true);
      } else {
        toast.error('Failed to update watchlist');
      }
    } finally {
      setWatchlistLoading(false);
    }
  };

  // Navigate to create alert
  const handleCreateAlert = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!isPro) {
      setUpgradeFeature('Alerts');
      setShowUpgradeModal(true);
      return;
    }
    
    navigate(`/app/alerts?symbol=${symbol.toUpperCase()}`);
  };

  // Fetch anomaly data
  // Now uses the overview endpoint's anomaly check for more reliable data
  const fetchAnomaly = useCallback(async () => {
    // First try to get from overview endpoint (includes has_anomaly check)
    // Then fall back to anomalies list if needed
    try {
      // First check the anomalies list (legacy approach)
      const response = await axios.get(`${API_BASE}/anomalies`, {
        params: { time_range: '3M' },  // Use 3 month range to capture older anomalies
        timeout: 5000  // 5 second timeout
      });
      // Handle new response structure: { anomalies: [...], ... }
      const anomaliesList = response.data.anomalies || response.data || [];
      const found = anomaliesList.find(a => a.symbol === symbol);
      if (found) {
        setAnomaly(found);
      } else {
        // If not in recent anomalies, try to get from direct symbol lookup
        try {
          const directResp = await axios.get(`${API_BASE}/anomaly/${symbol}`, { timeout: 3000 });
          if (directResp.data && directResp.data.symbol) {
            setAnomaly(directResp.data);
          } else {
            setAnomaly(null);
          }
        } catch {
          setAnomaly(null);
        }
      }
    } catch (error) {
      console.error('Error fetching anomaly:', error);
      setAnomaly(null);
    } finally {
      setAnomalyLoaded(true);
    }
  }, [symbol]);
  
  // Fetch overview data (includes quote, volatility, derived signals)
  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/stocks/${symbol}/overview`, { timeout: 8000 });
      if (response.data) {
        setOverview(response.data);
        // Update derived signals (NOT raw data)
        if (response.data.institutional_signal) {
          setInstitutionalSignal(response.data.institutional_signal);
        }
        if (response.data.political_signal) {
          setPoliticalSignal(response.data.political_signal);
        }
        // If price_metrics exists and we don't have anomaly data, use it as fallback
        if (response.data.price_metrics && response.data.has_anomaly) {
          setAnomaly(prev => prev || response.data.price_metrics);
        }
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    }
  }, [symbol]);

  // Fetch fast AI context
  const fetchFastContext = useCallback(async (anomalyData, signalData = null, forceRefresh = false, newsData = null) => {
    if (!anomalyData) {
      setFastLoading(false);
      setAiStatus('unavailable');
      setAiMessage('No anomaly data available for AI analysis');
      return;
    }
    
    setFastLoading(true);
    setAiStatus('pending');
    setAiMessage(null);
    
    try {
      // Build request payload - political_trades is optional and fetched server-side if needed
      const payload = {
        symbol,
        company_name: anomalyData.company_name || symbol,
        anomaly_data: {
          daily_return: anomalyData.daily_return || 0,
          volume_multiple: anomalyData.volume_multiple || 1,
          return_zscore: anomalyData.return_zscore || 0,
          activity_level: anomalyData.activity_level || 'Normal',
          last_candle_ts: anomalyData.last_candle_ts || ''
        },
        finnhub_news: newsData || finnhubNews,
        force_refresh: forceRefresh
        // Note: political_trades is no longer passed from frontend (Tier 1 compliance)
        // Backend will fetch internally if needed
      };
      
      const response = await axios.post(`${API_BASE}/fast-context`, payload, { timeout: 12000 });
      
      if (response.data) {
        setFastContext(response.data);
        setAiStatus('success');
      } else {
        setAiStatus('unavailable');
        setAiMessage('AI analysis could not be generated');
      }
    } catch (error) {
      console.error('Fast context error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setAiStatus('timeout');
        setAiMessage('AI analysis timed out. Try refreshing.');
      } else if (error.response?.status === 429) {
        setAiStatus('rate_limited');
        setAiMessage('AI service rate limited. Try again shortly.');
      } else {
        setAiStatus('error');
        setAiMessage('Unable to generate AI analysis');
      }
    } finally {
      setFastLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);  // Note: finnhubNews removed - passed directly as newsData param to avoid re-render loop

  // Fetch deep AI context (background)
  const fetchDeepContext = useCallback(async (anomalyData) => {
    if (!anomalyData) return;
    setDeepLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/deep-context`, {
        symbol,
        company_name: anomalyData.company_name || symbol,
        anomaly_data: {
          daily_return: anomalyData.daily_return || 0,
          volume_multiple: anomalyData.volume_multiple || 1,
          return_zscore: anomalyData.return_zscore || 0,
          activity_level: anomalyData.activity_level || 'Normal'
        }
        // Note: finnhubNews and fastContext not passed to avoid dependency loops
      });
      if (response.data) {
        setDeepContext(response.data);
      }
    } catch (error) {
      console.error('Deep context error:', error);
    } finally {
      setDeepLoading(false);
    }
  }, [symbol]);  // Removed finnhubNews and fastContext dependencies

  // Fetch political signal (DERIVED - no raw politician names/amounts)
  // Uses functional update to prevent unnecessary re-renders
  const fetchPoliticalSignal = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/political/${symbol}/signal`, { timeout: 5000 });
      if (response.data?.signal_available) {
        // Only update if data actually changed
        setPoliticalSignal(prev => {
          if (prev?.trades_last_30d === response.data.trades_last_30d &&
              prev?.signal_available === response.data.signal_available) {
            return prev; // No change, keep same reference
          }
          return response.data;
        });
      }
    } catch (error) {
      console.error('Political signal error:', error);
    }
  }, [symbol]);

  // Fetch institutional signal (DERIVED - no raw fund names/positions)
  const fetchInstitutionalSignal = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/institutional/${symbol}/signal`, { timeout: 5000 });
      if (response.data?.signal_available) {
        setInstitutionalSignal(response.data);
      }
    } catch (error) {
      console.error('Institutional signal error:', error);
    }
  }, [symbol]);

  // Fetch derived summaries (Tier 1 Compliance - rich data without raw details)
  const fetchSummaries = useCallback(async () => {
    setSummaryLoading(true);
    try {
      // Fetch both summaries in parallel with timeouts
      const [instResponse, polResponse] = await Promise.all([
        axios.get(`${API_BASE}/institutional/${symbol}/summary`, { params: { window_days: 60 }, timeout: 5000 }),
        axios.get(`${API_BASE}/political/${symbol}/summary`, { params: { window_days: 60 }, timeout: 5000 })
      ]);
      
      if (instResponse.data) {
        setInstitutionalSummary(instResponse.data);
      }
      if (polResponse.data) {
        setPoliticalSummary(polResponse.data);
      }
    } catch (error) {
      console.error('Summary fetch error:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [symbol]);

  // Fetch related news (runs in parallel with AI analysis)
  // Now works with or without anomaly data - uses defaults for general news lookup
  const fetchRelatedNews = useCallback(async (anomalyData = null, forceRefresh = false) => {
    setNewsLoading(true);
    setNewsStatus('pending');
    setNewsMessage(null);
    
    try {
      // Build move context - use anomaly data if available, otherwise use neutral defaults
      const moveContext = {
        direction: anomalyData?.daily_return >= 0 ? 'up' : 'neutral',
        magnitude: Math.abs(anomalyData?.daily_return || 0),
        volume_ratio: anomalyData?.volume_multiple || 1,
        timeframe: 'today'
      };
      
      const response = await axios.post(`${API_BASE}/related-news`, {
        symbol,
        move_context: moveContext,
        limit: 5
      }, { timeout: 10000 });
      
      if (response.data) {
        setRelatedNews(response.data);
        
        if (response.data.items && response.data.items.length > 0) {
          setNewsStatus('success');
        } else if (response.data.empty_message) {
          setNewsStatus('empty');
          setNewsMessage(response.data.empty_message);
        } else {
          setNewsStatus('empty');
          setNewsMessage('No recent news found for this stock');
        }
      }
    } catch (error) {
      console.error('Related news error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setNewsStatus('timeout');
        setNewsMessage('News request timed out. Try refreshing.');
      } else if (error.response?.status === 429) {
        setNewsStatus('rate_limited');
        setNewsMessage('Too many requests. Try again shortly.');
      } else {
        setNewsStatus('error');
        setNewsMessage('Unable to load news at this time.');
      }
      
      setRelatedNews({ items: [], empty_message: newsMessage || "Unable to load news at this time." });
    } finally {
      setNewsLoading(false);
    }
  }, [symbol, newsMessage]);

  // Fetch chart data - DERIVED DAILY CLOSE ONLY (Tier 1 Compliance)
  const fetchChartData = useCallback(async () => {
    try {
      setChartLoading(true);
      setChartError(null);
      
      const response = await axios.get(`${API_BASE}/stock/${symbol}/chart-daily`, { timeout: 5000 });
      
      if (response.data?.data?.length > 0) {
        setChartData(response.data.data);
      } else {
        setChartError('No data available');
      }
    } catch (error) {
      setChartError(error.response?.data?.detail || 'Failed to load chart');
    } finally {
      setLoading(false);
      setChartLoading(false);
    }
  }, [symbol]);

  // Initial data fetch
  useEffect(() => {
    fetchChartData();
    fetchAnomaly();
    fetchOverview();
    fetchPoliticalSignal();
    fetchInstitutionalSignal();
    fetchSummaries();
  }, [symbol, fetchChartData, fetchAnomaly, fetchOverview, fetchPoliticalSignal, fetchInstitutionalSignal, fetchSummaries]);

  // Fetch news first, then fast context (news is needed for AI synthesis)
  // Using a ref to track if we've already fetched to prevent re-fetching loops
  const hasFetchedContext = useRef(false);
  
  useEffect(() => {
    if (anomalyLoaded && !hasFetchedContext.current) {
      hasFetchedContext.current = true;
      
      // Always fetch related news (works with or without anomaly data)
      fetchRelatedNews(anomaly);
      
      if (anomaly) {
        // Fetch news first, then pass to fast context for proper synthesis
        const fetchNewsAndContext = async () => {
          try {
            // Fetch Finnhub news for this symbol
            const newsResponse = await axios.get(`${API_BASE}/stocks/${symbol}/news`, { timeout: 8000 });
            const newsData = newsResponse.data;
            
            if (newsData && newsData.articles && newsData.articles.length > 0) {
              // Store news and pass to fast context
              setFinnhubNews(newsData);
              fetchFastContext(anomaly, politicalSignal, false, newsData);
            } else {
              // No news available, still call fast context
              setFinnhubNews(null);
              fetchFastContext(anomaly, politicalSignal, false, null);
            }
          } catch (error) {
            console.error('News fetch error:', error);
            // Still call fast context even if news fails
            setFinnhubNews(null);
            fetchFastContext(anomaly, politicalSignal, false, null);
          }
        };
        
        fetchNewsAndContext();
      } else {
        // No anomaly data - AI features unavailable but news should still work
        setFastLoading(false);
        setAiStatus('unavailable');
        setAiMessage('No anomaly data available for this stock');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anomaly, anomalyLoaded, symbol]);  // Removed callback dependencies to prevent loops

  // Fetch deep context after fast context (delayed) - only once
  const hasFetchedDeep = useRef(false);
  
  useEffect(() => {
    if (fastContext && !deepContext && !deepLoading && !hasFetchedDeep.current) {
      hasFetchedDeep.current = true;
      const timer = setTimeout(() => {
        fetchDeepContext(anomaly);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fastContext, deepContext, deepLoading, anomaly, fetchDeepContext]);

  // Get signal values
  const getSignalStrength = () => {
    if (!anomaly) return { value: '-', color: '#71717a' };
    const score = anomaly.unusual_activity_score || 0;
    if (score >= 70) return { value: 'High', color: '#f87171' };
    if (score >= 40) return { value: 'Moderate', color: '#fbbf24' };
    return { value: 'Low', color: '#4ade80' };
  };

  const getTrendDirection = () => {
    if (!anomaly) return { value: '-', color: '#71717a', Icon: Minus };
    const ret = anomaly.daily_return || 0;
    if (ret > 1) return { value: 'Bullish', color: '#4ade80', Icon: TrendingUp };
    if (ret < -1) return { value: 'Bearish', color: '#f87171', Icon: TrendingDown };
    return { value: 'Neutral', color: '#71717a', Icon: Minus };
  };

  const getVolumeCondition = () => {
    if (!anomaly) return { value: '-', color: '#71717a' };
    const vol = anomaly.volume_multiple || 1;
    if (vol >= 2) return { value: 'Heavy', color: '#818cf8' };
    if (vol >= 1.5) return { value: 'Elevated', color: '#a78bfa' };
    return { value: 'Normal', color: '#71717a' };
  };

  const getAIBias = () => {
    if (!fastContext?.bias) return { value: '-', color: '#71717a' };
    const dir = fastContext.bias.direction;
    if (dir === 'upside') return { value: 'Upside', color: '#4ade80' };
    if (dir === 'downside') return { value: 'Downside', color: '#f87171' };
    return { value: 'Neutral', color: '#71717a' };
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#09090B' }}>
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#FAFAFA' }} />
      </div>
    );
  }

  const signal = getSignalStrength();
  const trend = getTrendDirection();
  const volume = getVolumeCondition();
  const bias = getAIBias();
  
  // Derived political activity flag for conditional rendering (non-flashing sections)
  const hasPoliticalActivity = politicalSignal?.signal_available && politicalSignal?.trades_last_30d > 0;

  // Dynamic SEO data for this stock
  const companyName = anomaly?.company_name || overview?.price_metrics?.company_name || symbol;
  const currentPrice = overview?.quote?.price;
  const stockStructuredData = getStockPageSchema(symbol, companyName, currentPrice);
  
  // Get company description for SEO
  const companyDescription = getCompanyDescriptionWithFallback(symbol, companyName);
  const seoDescription = companyDescription 
    ? `${companyDescription} Analyze ${symbol} stock with AI-driven insights and anomaly detection on ScopeTheMarket.`
    : `Analyze ${companyName} (${symbol}) stock with AI-driven insights, anomaly detection, institutional signals, and real-time market data on ScopeTheMarket.`;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#09090B' }}>
      <SEOHead
        title={`${symbol} Stock Analysis | ${companyName} | ScopeTheMarket`}
        description={seoDescription}
        canonical={`https://scopethemarket.com/app/stock/${symbol}`}
        ogTitle={`${symbol} Stock Analysis – ScopeTheMarket`}
        ogDescription={`Get AI-powered analysis for ${companyName} (${symbol}). Track unusual activity, volume spikes, institutional trading, and market trends.`}
        structuredData={stockStructuredData}
      />
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-xs mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white transition-colors" style={{ color: '#52525b' }}>
            Home
          </Link>
          <ChevronRight className="w-3 h-3" style={{ color: '#3f3f46' }} />
          <Link to="/stocks" className="hover:text-white transition-colors" style={{ color: '#52525b' }}>
            Stocks
          </Link>
          <ChevronRight className="w-3 h-3" style={{ color: '#3f3f46' }} />
          <span style={{ color: '#a1a1aa' }}>{symbol}</span>
        </nav>
        
        {/* Back button + Action Buttons */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate('/app')}
            variant="ghost"
            className="hover:bg-white/5 text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          {/* Watchlist & Alert Actions */}
          <div className="flex items-center gap-2">
            {/* Add to Watchlist Button */}
            <Button
              onClick={handleWatchlistToggle}
              variant={isInWatchlist ? "default" : "outline"}
              size="sm"
              disabled={watchlistLoading}
              className={isInWatchlist ? "bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/30" : ""}
              data-testid="watchlist-toggle-btn"
            >
              {watchlistLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isInWatchlist ? (
                <>
                  <Check className="w-4 h-4 mr-1.5" style={{ color: '#fbbf24' }} />
                  <span style={{ color: '#fbbf24' }}>Watching</span>
                </>
              ) : (
                <>
                  {isPro ? (
                    <Star className="w-4 h-4 mr-1.5" />
                  ) : (
                    <Lock className="w-3 h-3 mr-1.5" style={{ color: '#71717a' }} />
                  )}
                  <span>Watchlist</span>
                </>
              )}
            </Button>
            
            {/* Set Alert Button */}
            <Button
              onClick={handleCreateAlert}
              variant="outline"
              size="sm"
              data-testid="create-alert-btn"
            >
              {isPro ? (
                <Bell className="w-4 h-4 mr-1.5" />
              ) : (
                <Lock className="w-3 h-3 mr-1.5" style={{ color: '#71717a' }} />
              )}
              <span>Set Alert</span>
            </Button>
          </div>
        </div>

        {/* ========== SIGNAL SUMMARY BAR ========== */}
        <div 
          className="p-5 rounded-xl mb-6"
          style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          data-testid="signal-summary-bar"
          data-tour-element="stock-header"
        >
          {/* Top row: Company info + Live Price */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            {/* Left: Ticker, Move, Company */}
            <div className="flex-1" data-tour="sd-anomaly-move">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#FAFAFA' }}>
                  {symbol}
                </h1>
                {/* Show anomaly's daily_return (historical move) when available */}
                {(() => {
                  // Prioritize anomaly data since user clicked on a specific anomaly
                  const pct = anomaly?.daily_return ?? overview?.quote?.changePercent;
                  if (pct === null || pct === undefined) {
                    return (
                      <span className="text-2xl font-semibold" style={{ color: '#71717a' }}>
                        -
                      </span>
                    );
                  }
                  return (
                    <span 
                      className="text-2xl font-semibold"
                      style={{ color: pct >= 0 ? '#4ade80' : '#f87171' }}
                    >
                      {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                    </span>
                  );
                })()}
              </div>
              <p className="text-sm" style={{ color: '#71717a' }}>
                {anomaly?.company_name || overview?.price_metrics?.company_name || symbol}
              </p>
              {/* Company Description for SEO and user understanding */}
              {(() => {
                const desc = getCompanyDescriptionWithFallback(
                  symbol, 
                  anomaly?.company_name || overview?.price_metrics?.company_name
                );
                if (!desc) return null;
                return (
                  <p 
                    className="text-xs mt-1.5 max-w-xl leading-relaxed"
                    style={{ color: '#52525b' }}
                    data-testid="company-description"
                  >
                    {desc}
                  </p>
                );
              })()}
              
              {/* SEO Internal Link - Why is it moving */}
              <Link
                to={`/stocks/${symbol}/why-is-it-moving`}
                className="inline-flex items-center gap-1.5 text-xs mt-2 hover:underline transition-colors"
                style={{ color: '#60A5FA' }}
                data-testid="why-moving-link"
              >
                <TrendingUp className="w-3 h-3" />
                Why is {symbol} stock moving today?
              </Link>
              
              {/* Anomaly Date - when the price move happened */}
              {anomaly?.date && (
                <p className="text-[11px] mt-1" style={{ color: '#a78bfa' }}>
                  Detected {new Date(anomaly.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {anomaly.detected_at ? new Date(anomaly.detected_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '3:16 PM'} ET
                </p>
              )}
              {/* Fallback to detected_at if no date */}
              {!anomaly?.date && anomaly?.detected_at && (
                <p className="text-[11px] mt-1" style={{ color: '#a78bfa' }}>
                  Detected {new Date(anomaly.detected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(anomaly.detected_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} ET
                </p>
              )}
            </div>
            
            {/* Right: Delayed Price Module + Refresh button */}
            <div className="flex items-start gap-3" data-tour="sd-delayed-price">
              <LivePriceModule symbol={symbol} />
              
              {/* Refresh button */}
              <button 
                onClick={() => {
                  fetchFastContext(anomaly);
                  fetchDeepContext(anomaly);
                }}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                title="Refresh analysis"
              >
                <RefreshCw className="h-4 w-4" style={{ color: '#52525b' }} />
              </button>
            </div>
          </div>

          {/* Signal row */}
          <div className="flex flex-wrap items-center gap-3">
            <SignalBadge 
              label="Signal" 
              value={signal.value} 
              color={signal.color}
              icon={Activity}
            />
            <SignalBadge 
              label="Trend" 
              value={trend.value} 
              color={trend.color}
              icon={trend.Icon}
            />
            <SignalBadge 
              label="Volume" 
              value={volume.value} 
              color={volume.color}
              icon={BarChart3}
            />
            <SignalBadge 
              label="AI Bias" 
              value={bias.value} 
              color={bias.color}
              icon={Brain}
            />
          </div>
        </div>

        {/* ========== CHART + METRIC CARDS SIDE-BY-SIDE ========== */}
        <div className="flex flex-col lg:flex-row gap-6 mb-6">
          {/* Left: Chart (~70-75% on desktop) - DERIVED DAILY CLOSE ONLY */}
          <div 
            className="lg:w-[72%] p-5 rounded-xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            data-testid="price-chart-card"
            data-tour="sd-daily-chart"
          >
            <ChartErrorBoundary symbol={symbol} height="380px" onRetry={fetchChartData}>
              <DerivedPriceChart 
                data={chartData} 
                symbol={symbol}
                loading={chartLoading}
                error={chartError}
                onRetry={fetchChartData}
              />
            </ChartErrorBoundary>
          </div>

          {/* Right: Metric Cards (~28% on desktop, stacked vertically) */}
          <div className="lg:w-[28%] flex flex-col gap-3">
            {/* Volume */}
            {(() => {
              const vol = anomaly?.volume_multiple ?? overview?.price_metrics?.volume_multiple;
              const hasVolume = vol !== null && vol !== undefined;
              return (
                <MetricCard
                  title="Volume"
                  value={hasVolume ? `${vol.toFixed(1)}x` : '-'}
                  subtitle={
                    !hasVolume ? 'Data unavailable' :
                    vol >= 2 ? 'Heavy trading' : 
                    vol >= 1.5 ? 'Above average' : 
                    'Typical volume'
                  }
                  color={hasVolume ? '#818cf8' : '#71717a'}
                  icon={BarChart3}
                />
              );
            })()}

            {/* Volatility - Uses calculated sigma from overview */}
            {(() => {
              const sigma = overview?.volatility?.sigma ?? anomaly?.return_zscore ?? overview?.price_metrics?.return_zscore;
              const hasVolatility = sigma !== null && sigma !== undefined;
              return (
                <MetricCard
                  title="Volatility"
                  value={hasVolatility ? `${Math.abs(sigma).toFixed(1)}σ` : '-'}
                  subtitle={
                    !hasVolatility ? 'Data unavailable' :
                    Math.abs(sigma) >= 2 ? 'High deviation' : 
                    'Normal range'
                  }
                  color={hasVolatility ? '#f59e0b' : '#71717a'}
                  icon={Activity}
                />
              );
            })()}

            {/* AI Confidence Score - Enhanced with breakdown */}
            {(() => {
              const score = anomaly?.unusual_activity_score ?? overview?.price_metrics?.unusual_activity_score;
              const volMult = anomaly?.volume_multiple ?? overview?.price_metrics?.volume_multiple;
              const hasScore = score !== null && score !== undefined;
              
              return (
                <div 
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  data-testid="ai-confidence-score"
                  data-tour="sd-ai-confidence"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4" style={{ color: '#52525b' }} />
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: '#52525b' }}>AI Confidence Score</span>
                    </div>
                    <button
                      onClick={() => setShowConfidenceBreakdown(!showConfidenceBreakdown)}
                      className="p-1 rounded hover:bg-white/5 transition-colors"
                      title={showConfidenceBreakdown ? 'Hide breakdown' : 'Show breakdown'}
                    >
                      {showConfidenceBreakdown ? (
                        <ChevronUp className="h-3 w-3" style={{ color: '#52525b' }} />
                      ) : (
                        <Info className="h-3 w-3" style={{ color: '#52525b' }} />
                      )}
                    </button>
                  </div>
                  <div 
                    className="text-xl font-bold mb-1" 
                    style={{ color: score >= 70 ? '#f87171' : score >= 40 ? '#fbbf24' : hasScore ? '#4ade80' : '#71717a' }}
                  >
                    {hasScore ? score.toFixed(0) : '-'}
                  </div>
                  {/* Clarity interpretation */}
                  <p className="text-[11px] mb-2" style={{ color: '#71717a' }}>
                    {!hasScore 
                      ? 'No anomaly data available.'
                      : score >= 70 
                        ? 'High clarity behind today\'s move.'
                        : score >= 40 
                          ? 'Moderate clarity behind today\'s move.'
                          : 'Low clarity behind today\'s move.'}
                  </p>
                  
                  {/* Expandable Breakdown */}
                  {showConfidenceBreakdown && hasScore && (
                    <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px]" style={{ color: '#71717a' }}>Volume Anomaly</span>
                        <span className="text-[10px] font-medium" style={{ color: volMult >= 2 ? '#4ade80' : '#a1a1aa' }}>
                          {volMult >= 2 ? 'Strong' : volMult >= 1.5 ? 'Moderate' : 'Weak'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px]" style={{ color: '#71717a' }}>News Alignment</span>
                        <span className="text-[10px] font-medium" style={{ color: fastContext?.has_news ? '#4ade80' : '#a1a1aa' }}>
                          {fastContext?.has_news ? 'Aligned' : 'No data'}
                        </span>
                      </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px]" style={{ color: '#71717a' }}>Market Context</span>
                    <span className="text-[10px] font-medium" style={{ color: '#a1a1aa' }}>
                      {Math.abs(anomaly?.return_zscore || 0) >= 1.5 ? 'Unusual' : 'Normal'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px]" style={{ color: '#71717a' }}>Political Proximity</span>
                    <span className="text-[10px] font-medium" style={{ color: hasPoliticalActivity ? '#fbbf24' : '#a1a1aa' }}>
                      {hasPoliticalActivity ? 'Recent activity' : 'None'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Risk Level inline */}
              <div className="flex items-center gap-2 pt-2 mt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                <span className="text-[9px] uppercase" style={{ color: '#52525b' }}>Risk:</span>
                <span 
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                  style={{ 
                    backgroundColor: (anomaly?.risk?.label === 'Extreme' || anomaly?.risk?.label === 'Very High')
                      ? 'rgba(248, 113, 113, 0.1)' 
                      : anomaly?.risk?.label === 'High'
                        ? 'rgba(251, 191, 36, 0.1)' 
                        : 'rgba(74, 222, 128, 0.1)',
                    color: (anomaly?.risk?.label === 'Extreme' || anomaly?.risk?.label === 'Very High')
                      ? '#f87171' 
                      : anomaly?.risk?.label === 'High'
                        ? '#fbbf24' 
                        : '#4ade80'
                  }}
                >
                  {anomaly?.risk?.label || (Math.abs(anomaly?.return_zscore || 0) >= 2 ? 'High' : Math.abs(anomaly?.return_zscore || 0) >= 1 ? 'Medium' : 'Low')}
                </span>
              </div>
              
              {/* Voice AI Buttons - Non-intrusive inline */}
              <div className="flex items-center gap-2 pt-3 mt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          setIsPlaying(!isPlaying);
                          toast.info(isPlaying ? 'Audio stopped' : 'Playing summary...');
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] transition-colors"
                        style={{ 
                          backgroundColor: isPlaying ? 'rgba(129, 140, 248, 0.15)' : 'rgba(255,255,255,0.03)',
                          color: isPlaying ? '#818cf8' : '#71717a',
                          border: '1px solid rgba(255,255,255,0.05)'
                        }}
                        data-testid="voice-listen-button"
                      >
                        {isPlaying ? <VolumeX className="h-3 w-3" /> : <Headphones className="h-3 w-3" />}
                        {isPlaying ? 'Stop' : 'Listen'}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent style={{ backgroundColor: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <p className="text-xs" style={{ color: '#a1a1aa' }}>
                        {isPlaying ? 'Stop audio summary' : 'Listen to 30-sec summary'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setShowVoiceModule(!showVoiceModule)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] transition-colors"
                        style={{ 
                          backgroundColor: showVoiceModule ? 'rgba(74, 222, 128, 0.15)' : 'rgba(255,255,255,0.03)',
                          color: showVoiceModule ? '#4ade80' : '#71717a',
                          border: '1px solid rgba(255,255,255,0.05)'
                        }}
                        data-testid="voice-ask-button"
                      >
                        {showVoiceModule ? <MicOff className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
                        {showVoiceModule ? 'Close' : 'Ask'}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent style={{ backgroundColor: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <p className="text-xs" style={{ color: '#a1a1aa' }}>
                        {showVoiceModule ? 'Close voice assistant' : 'Ask about this stock'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                {/* Voice active indicator */}
                {voiceEnabled && (
                  <div className="flex items-center gap-1 ml-auto">
                    <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#4ade80' }} />
                    <span className="text-[9px]" style={{ color: '#52525b' }}>Voice on</span>
                  </div>
                )}
              </div>
              
              {/* Expandable Voice Module */}
              {showVoiceModule && (
                <div 
                  className="mt-3 p-3 rounded-lg"
                  style={{ backgroundColor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-medium" style={{ color: '#e4e4e7' }}>Voice Assistant</span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <span className="text-[9px]" style={{ color: '#52525b' }}>Wake word</span>
                      <button
                        onClick={() => setVoiceEnabled(!voiceEnabled)}
                        className="w-8 h-4 rounded-full transition-colors relative"
                        style={{ backgroundColor: voiceEnabled ? 'rgba(74, 222, 128, 0.3)' : 'rgba(255,255,255,0.1)' }}
                      >
                        <div 
                          className="w-3 h-3 rounded-full absolute top-0.5 transition-all"
                          style={{ 
                            backgroundColor: voiceEnabled ? '#4ade80' : '#52525b',
                            left: voiceEnabled ? '1rem' : '0.125rem'
                          }}
                        />
                      </button>
                    </label>
                  </div>
                  <p className="text-[10px] mb-3" style={{ color: '#71717a' }}>
                    Say "Hey Scope" or tap the mic to ask about {symbol}.
                  </p>
                  <div className="flex items-center justify-center py-4">
                    <button
                      onClick={() => {
                        setIsListening(!isListening);
                        if (!isListening) {
                          toast.info('Listening... Ask your question.');
                          // Auto-stop after 8 seconds
                          setTimeout(() => setIsListening(false), 8000);
                        }
                      }}
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                      style={{ 
                        backgroundColor: isListening ? 'rgba(248, 113, 113, 0.2)' : 'rgba(129, 140, 248, 0.15)',
                        border: isListening ? '2px solid #f87171' : '2px solid #818cf8',
                        animation: isListening ? 'pulse 1.5s infinite' : 'none'
                      }}
                    >
                      <Mic className="h-5 w-5" style={{ color: isListening ? '#f87171' : '#818cf8' }} />
                    </button>
                  </div>
                  <p className="text-[9px] text-center" style={{ color: '#52525b' }}>
                    {isListening ? 'Listening...' : 'Tap to speak'}
                  </p>
                  <div className="mt-3 pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-[9px]" style={{ color: '#3f3f46' }}>
                      Try: "Explain why it moved" or "Summarize the article"
                    </p>
                  </div>
                </div>
              )}
            </div>
            );
            })()}

            {/* Political Activity - DERIVED SIGNAL ONLY (Tier 1 Compliance) */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
              data-testid="political-activity-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <Landmark className="h-4 w-4" style={{ color: '#52525b' }} />
                <span className="text-[10px] uppercase tracking-wider" style={{ color: '#52525b' }}>Political Activity</span>
              </div>
              <div 
                className="text-xl font-bold mb-1" 
                style={{ color: hasPoliticalActivity ? '#fbbf24' : '#52525b' }}
              >
                {politicalSignal?.activity_score || 0}
              </div>
              <p className="text-[11px]" style={{ color: '#71717a' }}>
                {hasPoliticalActivity 
                  ? `${politicalSignal.trades_last_30d} trade${politicalSignal.trades_last_30d > 1 ? 's' : ''} in 30d • ${politicalSignal.net_activity.replace('_', ' ')}` 
                  : 'No recent activity'}
              </p>
              {/* Political Activity Level - DERIVED ONLY */}
              {hasPoliticalActivity && (
                <div className="mt-2 pt-2 border-t space-y-2" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase" style={{ color: '#52525b' }}>Activity (30d):</span>
                    <span 
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                      style={{ 
                        backgroundColor: politicalSignal.activity_score >= 60 
                          ? 'rgba(248, 113, 113, 0.1)' 
                          : politicalSignal.activity_score >= 30 
                            ? 'rgba(251, 191, 36, 0.1)' 
                            : 'rgba(74, 222, 128, 0.1)',
                        color: politicalSignal.activity_score >= 60 
                          ? '#f87171' 
                          : politicalSignal.activity_score >= 30 
                            ? '#fbbf24' 
                            : '#4ade80'
                      }}
                    >
                      {politicalSignal.activity_score >= 60 ? 'High' : politicalSignal.activity_score >= 30 ? 'Moderate' : 'Low'}
                    </span>
                  </div>
                  {/* Timing proximity - DERIVED (days ago, not dates) */}
                  {politicalSignal.last_reported_days_ago != null && (
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase" style={{ color: '#52525b' }}>Last filed:</span>
                      <span className="text-[10px]" style={{ color: '#71717a' }}>
                        {politicalSignal.last_reported_days_ago === 0 
                          ? 'Today' 
                          : politicalSignal.last_reported_days_ago === 1 
                            ? '1 day ago' 
                            : `${politicalSignal.last_reported_days_ago} days ago`}
                      </span>
                    </div>
                  )}
                  {/* Neutral disclaimer */}
                  <p className="text-[8px] pt-1" style={{ color: '#3f3f46' }}>
                    Aggregated signal. No evidence of direct causation.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ========== AI ANALYSIS - STRUCTURED SECTIONS ========== */}
        <div 
          className="p-5 rounded-xl mb-6"
          style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          data-testid="ai-analysis-section"
          data-tour="sd-ai-analysis"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4" style={{ color: '#fbbf24' }} />
              <span className="text-sm font-medium" style={{ color: '#e4e4e7' }}>AI Analysis</span>
              {fastContext?._cache === 'hit' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#52525b' }}>
                  cached
                </span>
              )}
              {/* Status indicator */}
              {aiStatus === 'timeout' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' }}>
                  timed out
                </span>
              )}
              {aiStatus === 'error' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(248, 113, 113, 0.15)', color: '#f87171' }}>
                  error
                </span>
              )}
            </div>
            
            {/* Retry button for failed AI */}
            {!fastLoading && (aiStatus === 'timeout' || aiStatus === 'error' || aiStatus === 'unavailable' || overview?.aiCard?.status === 'error' || overview?.aiCard?.status === 'partial') && anomaly && (
              <button
                onClick={() => {
                  // Re-fetch overview to get fresh aiCard
                  fetchOverview();
                  if (anomaly && politicalSignal) {
                    fetchFastContext(anomaly, politicalSignal, true);
                  }
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors hover:bg-white/5"
                style={{ color: '#71717a', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid="retry-ai-button"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </button>
            )}
          </div>

          {/* Loading skeleton */}
          {fastLoading && (
            <div className="space-y-4 animate-pulse">
              <div className="h-4 w-1/3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
              <div className="h-3 w-2/3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
              <div className="h-3 w-1/2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
            </div>
          )}
          
          {/* Empty/Error state - show instead of infinite skeleton */}
          {!fastLoading && !fastContext && (
            <div className="py-4 text-center">
              {aiStatus === 'timeout' && (
                <>
                  <Clock className="h-5 w-5 mx-auto mb-2" style={{ color: '#71717a' }} />
                  <p className="text-sm" style={{ color: '#71717a' }}>{aiMessage || 'AI analysis timed out'}</p>
                </>
              )}
              {aiStatus === 'error' && (
                <>
                  <p className="text-sm" style={{ color: '#71717a' }}>{aiMessage || 'Unable to generate AI analysis'}</p>
                </>
              )}
              {aiStatus === 'unavailable' && (
                <>
                  <p className="text-sm" style={{ color: '#71717a' }}>{aiMessage || 'No AI insight available for this stock'}</p>
                </>
              )}
              {aiStatus === 'pending' && !fastLoading && (
                <p className="text-sm" style={{ color: '#52525b' }}>AI analysis not yet loaded</p>
              )}
            </div>
          )}

          {/* Fast analysis content */}
          {!fastLoading && fastContext && (
            <div className="grid md:grid-cols-3 gap-6">
              {/* What moved */}
              <div>
                <h4 className="text-[10px] uppercase tracking-wider mb-2 font-medium" style={{ color: '#4ade80' }}>
                  What Moved
                </h4>
                <ul className="space-y-1.5">
                  <li className="text-sm" style={{ color: '#e4e4e7' }}>
                    {anomaly?.daily_return >= 0 ? 'Rose' : 'Fell'} {Math.abs(anomaly?.daily_return || 0).toFixed(1)}% today
                  </li>
                  <li className="text-sm" style={{ color: '#a1a1aa' }}>
                    Volume {anomaly?.volume_multiple?.toFixed(1) || 1}x normal
                  </li>
                </ul>
              </div>

              {/* Why it may have moved - Now uses cached aiCard from overview */}
              <div>
                <h4 className="text-[10px] uppercase tracking-wider mb-2 font-medium" style={{ color: '#818cf8' }}>
                  Why It May Have Moved
                </h4>
                <ul className="space-y-1.5">
                  {/* Prioritize aiCard data from overview, fallback to fastContext */}
                  {overview?.aiCard?.status === 'ready' && overview?.aiCard?.whyItMoved ? (
                    <li className="text-sm" style={{ color: '#e4e4e7' }}>
                      {overview.aiCard.whyItMoved}
                    </li>
                  ) : (overview?.aiCard?.status === 'partial' || overview?.aiCard?.status === 'no_news' || overview?.aiCard?.status === 'unavailable' || overview?.aiCard?.status === 'error') && overview?.aiCard?.whyItMoved ? (
                    <li className="text-sm" style={{ color: '#a1a1aa' }}>
                      {overview.aiCard.whyItMoved}
                    </li>
                  ) : fastContext?.summary?.bullets?.slice(0, 2).map((bullet, idx) => (
                    <li key={idx} className="text-sm" style={{ color: idx === 0 ? '#e4e4e7' : '#a1a1aa' }}>
                      {bullet}
                    </li>
                  ))}
                  {(!overview?.aiCard?.whyItMoved && (!fastContext?.summary?.bullets || fastContext.summary.bullets.length === 0)) && (
                    <li className="text-sm" style={{ color: '#a1a1aa' }}>
                      {fastContext?.has_news && fastContext?.has_political 
                        ? 'Recent news and political disclosures may be factors'
                        : fastContext?.has_news 
                          ? 'Recent news may be a factor' 
                          : fastContext?.has_political 
                            ? 'Recent political disclosures noted' 
                            : 'No clear catalyst detected'}
                    </li>
                  )}
                </ul>
                {/* AI Card status indicator */}
                {overview?.aiCard?.status === 'ready' && !overview?.aiCard?.isFallback && (
                  <div className="flex gap-2 mt-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1" 
                          style={{ backgroundColor: 'rgba(167, 139, 250, 0.1)', color: '#a78bfa' }}>
                      <Zap className="h-2.5 w-2.5" />
                      AI Summary
                    </span>
                  </div>
                )}
                {overview?.aiCard?.status === 'partial' && (
                  <div className="flex gap-2 mt-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1" 
                          style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24' }}>
                      <Zap className="h-2.5 w-2.5" />
                      Based on headlines
                    </span>
                  </div>
                )}
                {overview?.aiCard?.status === 'no_news' && (
                  <div className="flex gap-2 mt-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1" 
                          style={{ backgroundColor: 'rgba(113, 113, 122, 0.2)', color: '#71717a' }}>
                      No recent news
                    </span>
                  </div>
                )}
                {overview?.aiCard?.status === 'pending' && (
                  <div className="flex gap-2 mt-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1" 
                          style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24' }}>
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      Generating...
                    </span>
                  </div>
                )}
                {/* Legacy synthesis indicators for fastContext fallback */}
                {!overview?.aiCard?.whyItMoved && (fastContext?.has_news || fastContext?.has_political) && (
                  <div className="flex gap-2 mt-2">
                    {fastContext.has_news && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1" 
                            style={{ backgroundColor: 'rgba(96, 165, 250, 0.1)', color: '#60a5fa' }}>
                        <Newspaper className="h-2.5 w-2.5" />
                        News
                      </span>
                    )}
                    {fastContext.has_political && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1" 
                            style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24' }}>
                        <Landmark className="h-2.5 w-2.5" />
                        Political ({fastContext.political_count})
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* What to watch - Now uses cached aiCard from overview */}
              <div>
                <h4 className="text-[10px] uppercase tracking-wider mb-2 font-medium" style={{ color: '#fbbf24' }}>
                  What to Watch
                </h4>
                <ul className="space-y-1.5">
                  {/* Prioritize aiCard data from overview */}
                  {overview?.aiCard?.status === 'ready' && overview?.aiCard?.whatToWatch ? (
                    <li className="text-sm" style={{ color: '#e4e4e7' }}>
                      {overview.aiCard.whatToWatch}
                    </li>
                  ) : (overview?.aiCard?.status === 'partial' || overview?.aiCard?.status === 'no_news' || overview?.aiCard?.status === 'unavailable' || overview?.aiCard?.status === 'error') && overview?.aiCard?.whatToWatch ? (
                    <li className="text-sm" style={{ color: '#a1a1aa' }}>
                      {overview.aiCard.whatToWatch}
                    </li>
                  ) : (
                    <>
                      <li className="text-sm" style={{ color: '#e4e4e7' }}>
                        {fastContext?.summary?.bullets?.[2] || 'Monitor for follow-through'}
                      </li>
                      {fastContext?.bias?.signals?.length > 0 && (
                        <li className="text-sm" style={{ color: '#a1a1aa' }}>
                          Key: {fastContext.bias.signals.slice(0, 2).join(', ')}
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}

          {/* Deep analysis toggle */}
          {deepContext && (
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
              <button
                onClick={() => setShowDeep(!showDeep)}
                className="flex items-center gap-2 text-sm hover:bg-white/5 px-2 py-1 rounded transition-colors"
                style={{ color: '#71717a' }}
              >
                <Brain className="h-3.5 w-3.5" style={{ color: '#818cf8' }} />
                <span>Detailed Analysis</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDeep ? 'rotate-180' : ''}`} />
              </button>
              
              {showDeep && (
                <div className="mt-3 pl-6 space-y-3 text-sm" style={{ color: '#a1a1aa' }}>
                  {deepContext.analysis?.context && (
                    <p>{deepContext.analysis.context}</p>
                  )}
                  {deepContext.analysis?.drivers && (
                    <p>{deepContext.analysis.drivers}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ========== RELATED NEWS ROW ========== */}
          <div 
            className="mt-6 pt-5" 
            style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
            data-testid="news-section"
            data-tour="sd-related-news"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Newspaper className="h-4 w-4" style={{ color: '#60a5fa' }} />
                <span className="text-sm font-medium" style={{ color: '#e4e4e7' }}>Related News</span>
                {/* Status indicator */}
                {newsStatus === 'timeout' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' }}>
                    timed out
                  </span>
                )}
                {newsStatus === 'error' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(248, 113, 113, 0.15)', color: '#f87171' }}>
                    error
                  </span>
                )}
                {newsStatus === 'rate_limited' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' }}>
                    rate limited
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {/* Retry button for failed news */}
                {!newsLoading && (newsStatus === 'timeout' || newsStatus === 'error' || newsStatus === 'rate_limited') && anomaly && (
                  <button
                    onClick={() => fetchRelatedNews(anomaly, true)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] transition-colors hover:bg-white/5"
                    style={{ color: '#71717a', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="retry-news-button"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Retry
                  </button>
                )}
              
                {/* AI Article Analysis Button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const topArticle = relatedNews?.items?.[0];
                          // Pass fallback data for when URL fetch fails
                          const fallbackData = topArticle ? {
                            headline: topArticle.title || topArticle.headline,
                            summary: topArticle.summary || topArticle.description || '',
                            source: topArticle.source
                          } : null;
                          openArticleScanner(topArticle?.url || '', fallbackData);
                        }}
                        disabled={!relatedNews?.items?.length}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ 
                          backgroundColor: relatedNews?.items?.length ? 'rgba(96, 165, 250, 0.1)' : 'rgba(255,255,255,0.03)',
                          color: relatedNews?.items?.length ? '#60a5fa' : '#52525b',
                          border: '1px solid rgba(96, 165, 250, 0.2)'
                        }}
                        data-testid="ai-article-analysis-button"
                        data-tour="sd-ai-article-btn"
                      >
                        <FileSearch className="h-3.5 w-3.5" />
                        <span>AI Article Analysis</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent style={{ backgroundColor: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <p className="text-xs" style={{ color: '#a1a1aa' }}>
                        {relatedNews?.items?.length 
                          ? 'Analyze the most relevant article with AI'
                          : 'No relevant article to scan'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* News loading skeleton */}
            {newsLoading && (
              <div className="grid md:grid-cols-3 gap-4 animate-pulse">
                <div className="space-y-2">
                  <div className="h-3 w-3/4 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
                  <div className="h-2 w-1/2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }} />
                </div>
                <div className="h-3 w-full rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
                <div className="h-3 w-1/3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
              </div>
            )}

            {/* Empty/Error state - show clear message instead of skeleton */}
            {!newsLoading && (!relatedNews?.items || relatedNews?.items?.length === 0) && (
              <div className="py-3">
                {newsStatus === 'empty' && (
                  <p className="text-sm" style={{ color: '#71717a' }}>
                    {newsMessage || relatedNews?.empty_message || 'No recent news found for this stock'}
                  </p>
                )}
                {newsStatus === 'timeout' && (
                  <p className="text-sm" style={{ color: '#71717a' }}>
                    {newsMessage || 'News request timed out. Try refreshing.'}
                  </p>
                )}
                {newsStatus === 'error' && (
                  <p className="text-sm" style={{ color: '#71717a' }}>
                    {newsMessage || 'Unable to load news at this time.'}
                  </p>
                )}
                {newsStatus === 'rate_limited' && (
                  <p className="text-sm" style={{ color: '#71717a' }}>
                    {newsMessage || 'Too many requests. Try again shortly.'}
                  </p>
                )}
                {newsStatus === 'unavailable' && (
                  <p className="text-sm" style={{ color: '#71717a' }}>
                    {newsMessage || 'News not available for this stock.'}
                  </p>
                )}
              </div>
            )}

            {/* News Frame - Compact metadata row */}
            {!newsLoading && relatedNews?.news_frame && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-4 py-2 px-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                {/* AI Sentiment */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide" style={{ color: '#52525b' }}>AI Sentiment:</span>
                  <span 
                    className="text-xs font-medium px-1.5 py-0.5 rounded"
                    style={{ 
                      backgroundColor: relatedNews.news_frame.ai_sentiment === 'Positive' 
                        ? 'rgba(34, 197, 94, 0.15)' 
                        : relatedNews.news_frame.ai_sentiment === 'Negative' 
                          ? 'rgba(248, 113, 113, 0.15)' 
                          : 'rgba(161, 161, 170, 0.1)',
                      color: relatedNews.news_frame.ai_sentiment === 'Positive' 
                        ? '#22c55e' 
                        : relatedNews.news_frame.ai_sentiment === 'Negative' 
                          ? '#f87171' 
                          : '#a1a1aa'
                    }}
                  >
                    {relatedNews.news_frame.ai_sentiment}
                  </span>
                </div>
                
                <span className="text-[10px]" style={{ color: '#27272a' }}>•</span>
                
                {/* News Volume */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide" style={{ color: '#52525b' }}>News Volume:</span>
                  <span 
                    className="text-xs font-medium px-1.5 py-0.5 rounded"
                    style={{ 
                      backgroundColor: relatedNews.news_frame.news_volume === 'Extreme' 
                        ? 'rgba(251, 191, 36, 0.15)' 
                        : relatedNews.news_frame.news_volume === 'Elevated' 
                          ? 'rgba(96, 165, 250, 0.15)' 
                          : 'rgba(161, 161, 170, 0.1)',
                      color: relatedNews.news_frame.news_volume === 'Extreme' 
                        ? '#fbbf24' 
                        : relatedNews.news_frame.news_volume === 'Elevated' 
                          ? '#60a5fa' 
                          : '#a1a1aa'
                    }}
                  >
                    {relatedNews.news_frame.news_volume}
                    {relatedNews.news_frame.n24 > 0 && (
                      <span className="ml-1 opacity-60">({relatedNews.news_frame.n24})</span>
                    )}
                  </span>
                </div>
                
                <span className="text-[10px]" style={{ color: '#27272a' }}>•</span>
                
                {/* Catalyst Type */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide" style={{ color: '#52525b' }}>Catalyst:</span>
                  <span 
                    className="text-xs font-medium px-1.5 py-0.5 rounded"
                    style={{ 
                      backgroundColor: 'rgba(139, 92, 246, 0.15)',
                      color: '#a78bfa'
                    }}
                  >
                    {relatedNews.news_frame.catalyst_type}
                  </span>
                </div>
              </div>
            )}

            {/* News items - 3 column grid */}
            {!newsLoading && relatedNews?.items?.length > 0 && (
              <div className="space-y-4">
                {relatedNews.items.slice(0, 3).map((item, idx) => (
                  <div 
                    key={idx}
                    className="grid md:grid-cols-3 gap-4 py-3"
                    style={{ borderBottom: idx < relatedNews.items.length - 1 && idx < 2 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}
                  >
                    {/* Column 1: Headline + Source + Time */}
                    <div>
                      <a 
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium hover:underline line-clamp-2"
                        style={{ color: '#e4e4e7' }}
                      >
                        {item.title}
                      </a>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px]" style={{ color: '#52525b' }}>{item.source}</span>
                        <span className="text-[10px]" style={{ color: '#3f3f46' }}>•</span>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" style={{ color: '#3f3f46' }} />
                          <span className="text-[10px]" style={{ color: '#52525b' }}>
                            {(() => {
                              try {
                                const date = new Date(item.published_at);
                                const now = new Date();
                                const hours = Math.floor((now - date) / (1000 * 60 * 60));
                                if (hours < 24) return `${hours}h ago`;
                                const days = Math.floor(hours / 24);
                                return `${days}d ago`;
                              } catch {
                                return '';
                              }
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Summary + Why this relates */}
                    <div>
                      {item.snippet && (
                        <p className="text-xs line-clamp-2 mb-1.5" style={{ color: '#a1a1aa' }}>
                          {item.snippet.substring(0, 120)}...
                        </p>
                      )}
                      {item.why_relates && (
                        <p className="text-[10px] italic" style={{ color: '#71717a' }}>
                          → {item.why_relates}
                        </p>
                      )}
                    </div>

                    {/* Column 3: Impact tags + Relevance */}
                    <div className="flex flex-col items-start md:items-end gap-2">
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.tags.map((tag, tidx) => (
                            <span 
                              key={tidx}
                              className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded"
                              style={{ 
                                backgroundColor: 'rgba(96, 165, 250, 0.1)', 
                                color: '#60a5fa' 
                              }}
                            >
                              <Tag className="h-2.5 w-2.5" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {item.relevance_score && (
                        <span 
                          className="text-[9px] px-1.5 py-0.5 rounded"
                          style={{ 
                            backgroundColor: item.relevance_score >= 50 
                              ? 'rgba(34, 197, 94, 0.1)' 
                              : 'rgba(251, 191, 36, 0.1)',
                            color: item.relevance_score >= 50 ? '#4ade80' : '#fbbf24'
                          }}
                        >
                          {item.relevance_score >= 50 ? 'High' : 'Moderate'} relevance
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <p className="mt-4 text-[10px]" style={{ color: '#3f3f46' }}>
            For research only. Not financial advice.
          </p>
        </div>

        {/* ========== INSTITUTIONAL SUMMARY CARD (Tier 1 Compliance) ========== */}
        <div 
          className="p-5 rounded-xl mb-6"
          style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          data-testid="institutional-summary-section"
          data-tour="sd-institutional"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4" style={{ color: '#818cf8' }} />
              <span className="text-sm font-medium" style={{ color: '#e4e4e7' }}>Institutional Activity</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#4ade80' }}>
                Derived
              </span>
              {institutionalSummary?.is_estimated_data && (
                <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(129, 140, 248, 0.15)', color: '#818cf8' }}>
                  DEMO
                </span>
              )}
            </div>
            {institutionalSummary?.data_available && (
              <span className="text-[9px]" style={{ color: '#52525b' }}>
                Last {institutionalSummary.window_days} days
              </span>
            )}
          </div>

          {/* Loading state */}
          {summaryLoading && (
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-3/4 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
              <div className="h-3 w-1/2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
            </div>
          )}

          {/* Summary display */}
          {!summaryLoading && institutionalSummary && (
            <>
              {/* Summary text */}
              <p className="text-sm mb-4" style={{ color: '#a1a1aa', lineHeight: '1.6' }}>
                {institutionalSummary.summary_text}
              </p>

              {/* Metrics grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {/* Score */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Score</div>
                  <div className="text-xl font-bold" style={{ 
                    color: institutionalSummary.score >= 60 ? '#4ade80' 
                      : institutionalSummary.score >= 30 ? '#fbbf24' : '#71717a' 
                  }}>
                    {institutionalSummary.score}
                  </div>
                </div>

                {/* Sentiment */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Sentiment</div>
                  <div className="text-sm font-semibold capitalize" style={{ 
                    color: institutionalSummary.sentiment === 'accumulation' ? '#4ade80' 
                      : institutionalSummary.sentiment === 'distribution' ? '#f87171' 
                      : institutionalSummary.sentiment === 'mixed' ? '#fbbf24' : '#71717a' 
                  }}>
                    {institutionalSummary.sentiment}
                  </div>
                </div>

                {/* Increased */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Increased</div>
                  <div className="text-lg font-semibold" style={{ color: '#4ade80' }}>
                    {institutionalSummary.holders_increased_count}
                  </div>
                  <div className="text-[9px]" style={{ color: '#52525b' }}>holders</div>
                </div>

                {/* Decreased */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Decreased</div>
                  <div className="text-lg font-semibold" style={{ color: '#f87171' }}>
                    {institutionalSummary.holders_decreased_count}
                  </div>
                  <div className="text-[9px]" style={{ color: '#52525b' }}>holders</div>
                </div>
              </div>

              {/* Additional details */}
              <div className="flex flex-wrap gap-4 text-[10px]" style={{ color: '#71717a' }}>
                {institutionalSummary.holders_new_count > 0 && (
                  <span>
                    <span style={{ color: '#4ade80' }}>+{institutionalSummary.holders_new_count}</span> new position(s)
                  </span>
                )}
                {institutionalSummary.holders_exited_count > 0 && (
                  <span>
                    <span style={{ color: '#f87171' }}>{institutionalSummary.holders_exited_count}</span> exit(s)
                  </span>
                )}
                {institutionalSummary.net_holders_change !== 0 && (
                  <span>
                    Net: <span style={{ color: institutionalSummary.net_holders_change > 0 ? '#4ade80' : '#f87171' }}>
                      {institutionalSummary.net_holders_change > 0 ? '+' : ''}{institutionalSummary.net_holders_change}
                    </span>
                  </span>
                )}
                {institutionalSummary.last_update_days_ago != null && (
                  <span>Updated {institutionalSummary.last_update_days_ago}d ago</span>
                )}
              </div>
            </>
          )}

          {/* No data state */}
          {!summaryLoading && (!institutionalSummary || !institutionalSummary.data_available) && (
            <p className="text-sm py-4 text-center" style={{ color: '#71717a' }}>
              {institutionalSummary?.summary_text || 'No institutional data available.'}
            </p>
          )}

          {/* Compliance note */}
          <p className="mt-4 text-[10px]" style={{ color: '#3f3f46' }}>
            Aggregated institutional signals. No fund names or position sizes displayed.
          </p>
        </div>

        {/* ========== POLITICAL SUMMARY CARD (Tier 1 Compliance) ========== */}
        <div 
          className="p-5 rounded-xl mb-6"
          style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          data-testid="political-summary-section"
          data-tour="sd-political"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Landmark className="h-4 w-4" style={{ color: '#fbbf24' }} />
              <span className="text-sm font-medium" style={{ color: '#e4e4e7' }}>Political Activity</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#4ade80' }}>
                Derived
              </span>
              {politicalSummary?.is_estimated_data && (
                <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' }}>
                  DEMO
                </span>
              )}
            </div>
            {politicalSummary?.data_available && (
              <span className="text-[9px]" style={{ color: '#52525b' }}>
                Last {politicalSummary.window_days} days
              </span>
            )}
          </div>

          {/* Loading state */}
          {summaryLoading && (
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-3/4 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
              <div className="h-3 w-1/2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
            </div>
          )}

          {/* Summary display */}
          {!summaryLoading && politicalSummary && politicalSummary.data_available && (
            <>
              {/* Summary text */}
              <p className="text-sm mb-4" style={{ color: '#a1a1aa', lineHeight: '1.6' }}>
                {politicalSummary.summary_text}
              </p>

              {/* Metrics grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {/* Score */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Score</div>
                  <div className="text-xl font-bold" style={{ 
                    color: politicalSummary.score >= 60 ? '#fbbf24' 
                      : politicalSummary.score >= 30 ? '#a1a1aa' : '#71717a' 
                  }}>
                    {politicalSummary.score}
                  </div>
                </div>

                {/* Net Activity */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Direction</div>
                  <div className="text-sm font-semibold" style={{ 
                    color: politicalSummary.net_activity === 'net_buy' ? '#4ade80' 
                      : politicalSummary.net_activity === 'net_sell' ? '#f87171' 
                      : '#fbbf24' 
                  }}>
                    {politicalSummary.net_activity === 'net_buy' ? 'Net Buy' 
                      : politicalSummary.net_activity === 'net_sell' ? 'Net Sell' 
                      : 'Mixed'}
                  </div>
                </div>

                {/* Buys */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Purchases</div>
                  <div className="text-lg font-semibold" style={{ color: '#4ade80' }}>
                    {politicalSummary.buy_count}
                  </div>
                  <div className="text-[9px]" style={{ color: '#52525b' }}>filings</div>
                </div>

                {/* Sells */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#52525b' }}>Sales</div>
                  <div className="text-lg font-semibold" style={{ color: '#f87171' }}>
                    {politicalSummary.sell_count}
                  </div>
                  <div className="text-[9px]" style={{ color: '#52525b' }}>filings</div>
                </div>
              </div>

              {/* Additional details */}
              <div className="flex flex-wrap gap-4 text-[10px]" style={{ color: '#71717a' }}>
                <span>
                  <span style={{ color: '#fbbf24' }}>{politicalSummary.total_trades}</span> total disclosure(s)
                </span>
                {politicalSummary.unique_filers_count > 0 && (
                  <span>
                    <span style={{ color: '#818cf8' }}>{politicalSummary.unique_filers_count}</span> unique filer(s)
                  </span>
                )}
                {politicalSummary.last_activity_days_ago != null && (
                  <span>Most recent: {politicalSummary.last_activity_days_ago}d ago</span>
                )}
              </div>
            </>
          )}

          {/* No data state */}
          {!summaryLoading && (!politicalSummary || !politicalSummary.data_available) && (
            <p className="text-sm py-4 text-center" style={{ color: '#71717a' }}>
              {politicalSummary?.summary_text || 'No political trading disclosures found.'}
            </p>
          )}

          {/* Compliance note */}
          <p className="mt-4 text-[10px]" style={{ color: '#3f3f46' }}>
            Aggregated political trading activity. No politician names or transaction amounts displayed.
          </p>
        </div>

        {/* ========== FOOTER DISCLAIMER ========== */}
        <div className="pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.03)' }}>
          <p className="text-[10px] text-center" style={{ color: '#3f3f46' }}>
            For informational purposes only. This is not financial, investment, or trading advice.
          </p>
        </div>
      </div>

      {/* ========== ARTICLE ANALYZE MODAL ========== */}
      <ArticleAnalyzeModal
        isOpen={showArticleScan}
        onClose={() => {
          setShowArticleScan(false);
          setArticleScanUrl('');
          setArticleScanFallback(null);
        }}
        articleUrl={articleScanUrl}
        ticker={symbol}
        companyName={anomaly?.company_name || symbol}
        detectedTheme={relatedNews?.items?.[0]?.tags?.[0] || null}
        fallbackData={articleScanFallback}
        onAnalysisComplete={(result) => {
          setArticleAnalysisResult(result);
          toast.success('Article analysis complete');
        }}
      />
      
      {/* Upgrade Modal for Pro Features */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
      
      {/* Related Stocks Section - Internal Linking for SEO */}
      {(() => {
        const sectorInfo = findStockSector(symbol);
        const relatedStocks = getRelatedStocks(symbol, 8);
        
        if (!sectorInfo || relatedStocks.length === 0) return null;
        
        return (
          <div 
            className="mt-8 p-5 rounded-xl mx-auto max-w-[1400px]"
            style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            data-testid="related-stocks-section"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: sectorInfo.color }}
                />
                <h3 className="text-sm font-medium" style={{ color: '#e4e4e7' }}>
                  More {sectorInfo.sector} Stocks
                </h3>
              </div>
              <Link 
                to="/stocks"
                className="text-xs hover:underline transition-colors"
                style={{ color: '#71717a' }}
              >
                View all S&P 500 →
              </Link>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {relatedStocks.map((stock) => (
                <Link
                  key={stock.symbol}
                  to={`/app/stock/${stock.symbol}`}
                  className="px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: stock.color
                  }}
                  data-testid={`related-stock-${stock.symbol}`}
                >
                  {stock.symbol}
                </Link>
              ))}
            </div>
            
            <p className="mt-3 text-[10px]" style={{ color: '#52525b' }}>
              Explore other {sectorInfo.sector} companies in the S&P 500
            </p>
          </div>
        );
      })()}
      
      {/* Financial Disclaimer */}
      <div className="mt-8 mb-4 mx-auto max-w-4xl px-4">
        <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(148, 163, 184, 0.05)', border: '1px solid rgba(148, 163, 184, 0.1)' }}>
          <p className="text-xs leading-relaxed text-center" style={{ color: '#64748B' }}>
            <strong style={{ color: '#94A3B8' }}>Disclaimer:</strong> This information is for research and educational purposes only. 
            It does not constitute investment advice or a recommendation to buy or sell any security. 
            Past performance does not guarantee future results. Users should conduct their own research and consult a qualified 
            financial advisor before making investment decisions. ScopeTheMarket is not liable for any losses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StockDetail;
